import { ShoppingCart, Search, User, Package, LogOut, ChevronDown, Heart, Menu, BookOpen, Home, Gem } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchProducts, Product } from "@/services/productService";
import { getProductPath } from "@/lib/productUrls";
import { getCategoryPath, getProductCategory, PRODUCT_CATEGORIES, safelyDecodeCategorySlug } from "@/lib/productCategories";
import { createCategoryBarScrollState, updateCategoryBarScrollState } from "@/lib/categoryBarVisibility";
import OptimizedImage from "@/components/OptimizedImage";
import AuthDialog from "./AuthDialog";
import { AppPromotionStrip } from "./AppPromotionStrip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import auraLogo from "@/assets/uma-aura-logo.svg";

const SEARCH_TERMS = ["Bracelets", "Idols", "Gemstones", "Yantras", "Malas"];

interface NavbarProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  productCategoryKey?: string;
}

const Navbar = ({ searchQuery = "", onSearchChange, productCategoryKey }: NavbarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isProductRoute = location.pathname.startsWith("/product/");
  const routeCategory = location.pathname.startsWith("/category/")
    ? getProductCategory(safelyDecodeCategorySlug(location.pathname.split("/")[2] || ""))
    : undefined;
  const hideCategoryNav = !(isHome || isProductRoute || location.pathname.startsWith("/category/"));
  const isBlogActive = location.pathname.startsWith("/blog");
  const isGemfinderActive = location.pathname === "/gemfinder";
  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { isAuthenticated, userPhone, isAdmin, logout } = useAuth();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  // Active category starts as null (inactive on load & when viewing hero section)
  const [homeActiveCategoryKey, setHomeActiveCategoryKey] = useState<string | null>(null);
  const [isCategoryBarVisible, setIsCategoryBarVisible] = useState(true);
  const pillRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const categoryScrollerRef = useRef<HTMLDivElement>(null);
  const categoryNavRef = useRef<HTMLElement>(null);
  const activeCategoryKey = isHome
    ? homeActiveCategoryKey
    : routeCategory?.key ?? (isProductRoute ? getProductCategory(productCategoryKey)?.key ?? null : null);

  useEffect(() => {
    let scrollState = createCategoryBarScrollState(window.scrollY);
    let ticking = false;
    let frame: number | undefined;
    setIsCategoryBarVisible(true);

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      frame = window.requestAnimationFrame(() => {
        const nextY = Math.max(window.scrollY, 0);
        scrollState = updateCategoryBarScrollState(scrollState, nextY, isHome);
        setIsCategoryBarVisible(scrollState.visible);
        ticking = false;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (frame !== undefined) window.cancelAnimationFrame(frame);
    };
  }, [isHome]);

  useEffect(() => {
    const nav = categoryNavRef.current;
    if (!nav) return;
    if (isHome && !isCategoryBarVisible) nav.setAttribute("inert", "");
    else nav.removeAttribute("inert");
  }, [isCategoryBarVisible, isHome]);

  // Scroll-Spy logic: clear active category when viewing hero/top, activate section pill when in view
  useEffect(() => {
    if (typeof window === "undefined" || window.location.pathname !== "/") {
      setHomeActiveCategoryKey(null);
      return;
    }

    const categoryKeys = PRODUCT_CATEGORIES.map((c) => c.key);

    const checkScrollSpy = () => {
      const scrollY = Math.max(window.scrollY, 0);
      const firstSectionElem = document.getElementById("bracelet");

      if (firstSectionElem) {
        const firstTop = firstSectionElem.getBoundingClientRect().top + scrollY;
        // Header height is ~80px (main) + ~48px (category) = 128px
        // When scroll position is above the first category section, clear all active pills
        if (scrollY < firstTop - 180) {
          setHomeActiveCategoryKey(null);
          return;
        }
      } else {
        setHomeActiveCategoryKey(null);
        return;
      }

      // Determine which product section is currently visible under header (~130px)
      let currentActive: string | null = null;
      let minDistance = Infinity;

      categoryKeys.forEach((key) => {
        const elem = document.getElementById(key);
        if (elem) {
          const rect = elem.getBoundingClientRect();
          const topDiff = Math.abs(rect.top - 140);
          if (rect.top <= 220 && rect.bottom >= 120) {
            if (topDiff < minDistance) {
              minDistance = topDiff;
              currentActive = key;
            }
          }
        }
      });

      setHomeActiveCategoryKey(currentActive);
    };

    window.addEventListener("scroll", checkScrollSpy, { passive: true });
    checkScrollSpy();

    return () => window.removeEventListener("scroll", checkScrollSpy);
  }, [isHome]);

  // Scroll active category pill horizontally into view on mobile
  useEffect(() => {
    const activeKey = activeCategoryKey || (isHome ? "home" : "");
    const pillElem = pillRefs.current[activeKey];
    const scroller = categoryScrollerRef.current;
    if (pillElem && scroller) {
      const isReducedMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const targetLeft = pillElem.offsetLeft - scroller.clientWidth / 2 + pillElem.offsetWidth / 2;
      scroller.scrollTo({
        left: Math.max(0, Math.min(targetLeft, scroller.scrollWidth - scroller.clientWidth)),
        behavior: isReducedMotion ? "auto" : "smooth",
      });
    }
  }, [activeCategoryKey, isHome]);

  const { data: allProducts = [] } = useQuery({
    queryKey: ["products"],
    queryFn: () => fetchProducts(""),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const query = (onSearchChange ? searchQuery : localQuery).trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (!query) return [];
    return allProducts
      .filter((p) => {
        const name = (p.name || '').toLowerCase();
        const cat = (p.category || '').toLowerCase();
        return name.includes(query) || cat.includes(query);
      })
      .slice(0, 8);
  }, [allProducts, query]);

  // close suggestions when click outside
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target as Node)) {
        setIsSearchFocused(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleQueryChange = (v: string) => {
    setLocalQuery(v);
    onSearchChange?.(v);
  };

  const goToProduct = (product: Product) => {
    setIsSearchFocused(false);
    setLocalQuery('');
    onSearchChange?.('');
    navigate(getProductPath(product));
  };

  const logoRef = useRef<HTMLImageElement>(null);
  const [taglineWidth, setTaglineWidth] = useState<number>();
  
  useEffect(() => {
    const update = () => {
      if (logoRef.current) {
        setTaglineWidth(logoRef.current.getBoundingClientRect().width);
      }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSearchIndex((prev) => (prev + 1) % SEARCH_TERMS.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      {/* Main White Header Container */}
      <header
        className="sticky top-0 z-50 m-0 border-b border-border bg-white p-0 shadow-sm"
        style={{
          paddingInlineStart: "env(safe-area-inset-left)",
          paddingInlineEnd: "env(safe-area-inset-right)",
        }}
      >
        <div className="container mx-auto min-w-0 px-2 sm:px-4">
          <div className="flex h-20 min-w-0 items-center justify-between gap-1.5 sm:gap-3 lg:gap-4">
            {/* Mobile Menu Button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 shrink-0 lg:hidden"
                  aria-label="Open navigation menu"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[min(88vw,320px)] overflow-y-auto bg-background">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    Explore
                  </p>
                  <Link
                    to="/guides/index.html"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-current={isBlogActive ? "page" : undefined}
                  className={`flex min-h-12 items-center gap-3 border-y px-2 py-3 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 ${
                      isBlogActive
                        ? "border-primary/40 bg-primary/10 text-secondary"
                        : "border-border text-foreground hover:bg-muted hover:text-secondary"
                    }`}
                  >
                    <BookOpen className="h-5 w-5 text-primary" aria-hidden="true" />
                    <span>Blog</span>
                  </Link>
                  <Link
                    to="/gemfinder"
                    onClick={() => setMobileMenuOpen(false)}
                    aria-current={isGemfinderActive ? "page" : undefined}
                    className={`flex min-h-12 items-center gap-3 border-b px-2 py-3 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 ${
                      isGemfinderActive ? "border-primary/40 bg-primary/10 text-secondary" : "border-border text-foreground hover:bg-muted hover:text-secondary"
                    }`}
                  >
                    <Gem className="h-5 w-5 text-primary" aria-hidden="true" />
                    <span>Gem Finder</span>
                  </Link>
                </div>
                <div className="mt-6 flex flex-col gap-1">
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    Shop categories
                  </p>
                  <Link
                    to="/"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex min-h-11 items-center gap-2 rounded-md px-2 py-2 text-base font-semibold text-foreground transition-colors hover:bg-muted hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                  >
                    <Home className="h-4 w-4" aria-hidden="true" /> Home
                  </Link>
                  {PRODUCT_CATEGORIES.map((category) => (
                    <Link
                      key={category.key}
                      to={getCategoryPath(category.key)}
                      className="min-h-11 rounded-md px-2 py-2 text-left text-base font-semibold text-foreground transition-colors hover:bg-muted hover:text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {category.navLabel}
                    </Link>
                  ))}
                </div>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <div className="min-w-0 flex-shrink-0">
              <Link
                to="/"
                aria-label="Uma's Aura home"
                className="flex min-w-0 flex-col items-center gap-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
              >
                <img ref={logoRef} src={auraLogo} alt="Uma's Aura" width={613} height={234} className="h-9 w-auto max-w-[96px] sm:h-12 sm:max-w-none lg:h-14" />
                <div
                  className="hidden items-center gap-2 text-[9px] sm:flex lg:text-[10px]"
                  style={{ width: taglineWidth ? `${taglineWidth}px` : undefined, color: 'hsl(var(--brand-gold))' }}
                >
                  <div className="h-[1px] flex-1" style={{ backgroundColor: 'hsl(var(--brand-gold))' }}></div>
                  <span className="font-semibold whitespace-nowrap">Because You Deserve The Finest</span>
                  <div className="h-[1px] flex-1" style={{ backgroundColor: 'hsl(var(--brand-gold))' }}></div>
                </div>
              </Link>
            </div>

            {/* Search Bar — dynamic suggestions (desktop only) */}
            <div className="hidden min-w-0 max-w-2xl flex-1 lg:block" ref={searchWrapperRef}>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                  type="search"
                  aria-label="Search products"
                  className="w-full pl-11 pr-4 py-2 h-11 bg-muted/40 border border-input rounded-full focus-visible:ring-2 focus-visible:ring-primary/40 text-base"
                  value={onSearchChange ? searchQuery : localQuery}
                  onChange={(e) => handleQueryChange(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  placeholder={isSearchFocused || (onSearchChange ? searchQuery : localQuery) ? "Search products..." : ""}
                />
                {!isSearchFocused && !(onSearchChange ? searchQuery : localQuery) && (
                  <div className="absolute left-11 top-1/2 -translate-y-1/2 flex items-center pointer-events-none text-base">
                    <span className="text-muted-foreground">Search </span>
                    <div className="overflow-hidden h-6 ml-1">
                      <div
                        className="transition-transform duration-300 ease-in-out"
                        style={{ transform: `translateY(-${currentSearchIndex * 24}px)` }}
                      >
                        {SEARCH_TERMS.map((term, index) => (
                          <div key={index} className="h-6 flex items-center text-amber-700 font-semibold">
                            {term}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Suggestions dropdown */}
                {query && (
                  <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl border border-border bg-white shadow-xl overflow-hidden">
                    {suggestions.length === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                        No products match “{query}”
                      </div>
                    ) : (
                      <ul className="max-h-96 overflow-y-auto">
                        {suggestions.map((p) => (
                          <li key={p.id}>
                            <button
                              type="button"
                              onMouseDown={(e) => { e.preventDefault(); goToProduct(p); }}
                              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60 transition-colors text-left"
                            >
                              <div className="h-10 w-10 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                                {p.image && (
                                  <OptimizedImage
                                    src={p.image}
                                    alt=""
                                    sizes="48px"
                                    className="w-full h-full object-cover"
                                  />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">{p.name}</p>
                                {p.category && (
                                  <p className="text-xs text-muted-foreground truncate">{p.category}</p>
                                )}
                              </div>
                              <span className="text-sm font-semibold text-primary whitespace-nowrap">
                                ₹{p.price?.toLocaleString('en-IN')}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Action Icons */}
            <div className="flex shrink-0 items-center gap-0.5 sm:gap-1 xl:gap-3">
              {/* Blog */}
              <Link
                to="/guides/index.html"
                aria-label="Read the Uma's Aura blog"
                aria-current={isBlogActive ? "page" : undefined}
                className={`hidden min-h-11 min-w-11 flex-col items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 lg:flex ${
                  isBlogActive ? "bg-primary/10 text-secondary" : "hover:bg-muted hover:text-secondary"
                }`}
              >
                <BookOpen className="h-5 w-5" aria-hidden="true" />
                <span>Blog</span>
              </Link>

              <Link
                to="/gemfinder"
                aria-label="Open the Uma's Aura Gem Finder"
                aria-current={isGemfinderActive ? "page" : undefined}
                className={`hidden min-h-11 min-w-11 flex-col items-center justify-center gap-1 rounded-md px-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 lg:flex ${
                  isGemfinderActive ? "bg-primary/10 text-secondary" : "hover:bg-muted hover:text-secondary"
                }`}
              >
                <Gem className="h-5 w-5" aria-hidden="true" />
                <span>Gem Finder</span>
              </Link>

              {/* Profile/Auth */}
              {isAuthenticated ? (
                <DropdownMenu onOpenChange={(open) => {
                  if (open && isAdmin()) {
                    void import("@/pages/admin/AdminOrders").catch(() => undefined);
                  }
                }}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex min-h-11 min-w-11 flex-col items-center justify-center gap-1 rounded-md p-1 transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
                      aria-label="Open profile menu"
                    >
                      <User className="h-5 w-5" />
                      <span className="hidden items-center gap-0.5 text-xs font-semibold lg:flex">
                        Profile
                        <ChevronDown className="h-3 w-3" />
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      {userPhone && `+91 ${userPhone}`}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/profile">
                        <User className="mr-2 h-4 w-4" />
                        My Profile
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin() && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/orders">
                            <Package className="mr-2 h-4 w-4" />
                            Admin Panel
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/orders">
                        <Package className="mr-2 h-4 w-4" />
                        Order History
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <button 
                  className="flex min-h-11 min-w-11 flex-col items-center justify-center gap-1 rounded-md p-1 transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
                  onClick={() => setAuthDialogOpen(true)}
                  aria-label="Sign in or open profile"
                >
                  <User className="h-5 w-5" />
                  <span className="hidden text-xs font-semibold lg:block">Profile</span>
                </button>
              )}

              {/* Wishlist */}
              <button 
                className="relative flex min-h-11 min-w-11 flex-col items-center justify-center gap-1 rounded-md p-1 transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
                onClick={() => navigate("/wishlist")}
                aria-label={`Wishlist${wishlistCount > 0 ? `, ${wishlistCount} items` : ""}`}
              >
                <Heart className="h-5 w-5" />
                <span className="hidden text-xs font-semibold lg:block">Wishlist</span>
                {wishlistCount > 0 && (
                  <span className="absolute -top-0.5 right-0 sm:-right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center">
                    {wishlistCount}
                  </span>
                )}
              </button>

              {/* Cart */}
              <button 
                className="relative flex min-h-11 min-w-11 flex-col items-center justify-center gap-1 rounded-md p-1 transition-colors hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2"
                onClick={() => navigate("/cart")}
                aria-label={`Cart${cartCount > 0 ? `, ${cartCount} items` : ""}`}
                data-cart-target
              >
                <ShoppingCart className="h-5 w-5" />
                <span className="hidden text-xs font-semibold lg:block">Cart</span>
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 right-0 sm:-right-1 h-4 w-4 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Premium Glassmorphism Scroll-Spy Category Navbar */}
      {!hideCategoryNav && (
        <nav
          ref={categoryNavRef}
          aria-label="Shop categories"
          aria-hidden={isHome && !isCategoryBarVisible ? true : undefined}
          className={`sticky top-[80px] z-40 border-b border-white/25 bg-white/10 px-2 py-2 shadow-[0_8px_20px_rgba(45,35,20,0.08)] backdrop-blur-sm transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none sm:px-4 ${
            isHome && !isCategoryBarVisible ? "pointer-events-none -translate-y-full opacity-0" : "translate-y-0 opacity-100"
          }`}
        >
          <div className="relative w-full overflow-hidden">
            <div ref={categoryScrollerRef} className="overflow-x-auto overscroll-x-contain scrollbar-none py-1 px-1">
              <div className="flex min-w-max items-center gap-2 sm:gap-3 justify-start md:justify-center">
                <Link
                  ref={(element) => { pillRefs.current.home = element; }}
                  to="/"
                  aria-current={isHome && !activeCategoryKey ? "page" : undefined}
                  tabIndex={isHome && !isCategoryBarVisible ? -1 : undefined}
                  className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-xs transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 sm:h-9 sm:px-4 sm:text-sm ${
                    isHome && !activeCategoryKey
                      ? "border border-amber-300/80 bg-gradient-to-r from-amber-500 to-yellow-600 font-bold text-black shadow-md shadow-amber-500/20"
                      : "border border-white/60 bg-white/80 font-medium text-stone-800 hover:-translate-y-0.5 hover:border-amber-400/70 hover:bg-white/95 hover:text-amber-900"
                  }`}
                >
                  <Home className="h-3.5 w-3.5" aria-hidden="true" /> Home
                </Link>
                {PRODUCT_CATEGORIES.map((category) => {
                  const isActive = activeCategoryKey === category.key;
                  return (
                    <Link
                      key={category.key}
                      ref={(el) => {
                        pillRefs.current[category.key] = el;
                      }}
                      to={getCategoryPath(category.key)}
                      aria-current={routeCategory?.key === category.key ? "page" : undefined}
                      tabIndex={isHome && !isCategoryBarVisible ? -1 : undefined}
                      className={`inline-flex h-8 sm:h-9 items-center whitespace-nowrap rounded-full px-3 sm:px-4 text-xs sm:text-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 cursor-pointer ${
                        isActive
                          ? "border border-amber-300/80 bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-bold shadow-md shadow-amber-500/20"
                          : "border border-white/60 bg-white/80 font-medium text-stone-800 hover:-translate-y-0.5 hover:scale-[1.02] hover:border-amber-400/70 hover:bg-white/95 hover:text-amber-900 hover:shadow-md hover:shadow-amber-500/10"
                      }`}
                    >
                      {category.navLabel}
                    </Link>
                  );
                })}
              </div>
            </div>
            {/* Subtle right edge fade indicator on mobile */}
            <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white/40 to-transparent md:hidden" />
          </div>
        </nav>
      )}

      {!hideCategoryNav && <AppPromotionStrip />}

      {/* Auth Dialog */}
      <AuthDialog open={authDialogOpen} onOpenChange={setAuthDialogOpen} />
    </>
  );
};

export default Navbar;
